import { CampaignTimeUnits } from '@/Constants/Campaigns.js';
import { PromotionsType } from '@/Constants/Promotions.js';
import { getDivisor } from '@/Services/Helpers.js';
import { notify } from '@/Services/Notify.js';

// validation checks performed PRIOR to creating/updating a campaign
export function performCampaignRulesCheck(campaignPayload) {
	if (campaignPayload.campaign_type_id == PromotionsType.NextDepositMatch) {
		let returnPayload = {};
		if (campaignPayload.offer_rules.max_payout_percentage > 200) {
			returnPayload = {
				...returnPayload,
				...{
					'offer_rules.max_payout_percentage': ['Max payout percentage is above the maximum allowed']
				}
			};
		}
		if (campaignPayload.offer_rules.max_bonus_amount > 10000) {
			returnPayload = {
				...returnPayload,
				...{
					'offer_rules.max_bonus_amount': ['Max bonus amount is above the maximum allowed']
				}
			};
		}

		if (Object.keys(returnPayload).length > 0) {
			notify({
				type: 'danger',
				title: 'Error!',
				message: 'The selected offer rules are above the maximum allowed',
				timeout: 3
			});
			return returnPayload;
		}
	}

	return true;
}

// Function to take a cloned raw campaign payload and massage it to a prepared one.
export function prepareCampaignPayload(campaignPayload) {
	// First we clean the data to only deal with data that is required.
	const filteredCriteria = {};
	for (const criteria in campaignPayload.user_criteria) {
		if (
			'operator' in campaignPayload.user_criteria[criteria] &&
			campaignPayload.user_criteria[criteria].operator != null &&
			(('value' in campaignPayload.user_criteria[criteria] &&
				campaignPayload.user_criteria[criteria].operator != 'between' &&
				campaignPayload.user_criteria[criteria].value != null &&
				!(
					Array.isArray(campaignPayload.user_criteria[criteria].value) &&
					campaignPayload.user_criteria[criteria].value.length === 0
				)) ||
				('value' in campaignPayload.user_criteria[criteria] &&
					'value_between' in campaignPayload.user_criteria[criteria] &&
					campaignPayload.user_criteria[criteria].operator == 'between' &&
					campaignPayload.user_criteria[criteria].value != null &&
					campaignPayload.user_criteria[criteria].value_between != null))
		) {
			filteredCriteria[criteria] = campaignPayload.user_criteria[criteria];

			// First we filter out the unneeded key/value pairs (remove value_between when operator isn't between)
			if ('value_between' in filteredCriteria[criteria] && filteredCriteria[criteria].operator != 'between') {
				delete filteredCriteria[criteria].value_between;
			}

			// Convert any numeric values to floats instead of strings.
			if (
				'value' in filteredCriteria[criteria] &&
				!Array.isArray(filteredCriteria[criteria].value) &&
				parseFloat(filteredCriteria[criteria].value)
			) {
				filteredCriteria[criteria].value = parseFloat(filteredCriteria[criteria].value);
			}
			// Convert any numeric value_between to floats instead of strings.
			if (
				'value_between' in filteredCriteria[criteria] &&
				!Array.isArray(filteredCriteria[criteria].value) &&
				parseFloat(filteredCriteria[criteria].value_between)
			) {
				filteredCriteria[criteria].value_between = parseFloat(filteredCriteria[criteria].value_between);
			}
		}
	}
	campaignPayload.user_criteria = filteredCriteria;

	// When the expires is none the expires in values (being required) needs to be set to something.
	if (campaignPayload.expires_in_unit_id === CampaignTimeUnits.NONE) {
		campaignPayload.expires_in = 0;
	}

	// Convert currency fields to cents (handled PER campaign type)
	if (campaignPayload.campaign_type_id == PromotionsType.NextDepositMatch) {
		if ('max_bonus_amount' in campaignPayload.offer_rules) {
			campaignPayload.offer_rules.max_bonus_amount = parseInt(
				(parseFloat(campaignPayload.offer_rules.max_bonus_amount.toString()) * getDivisor()).toString()
			);
		}

		if ('lifetime_net_win' in campaignPayload.user_criteria) {
			campaignPayload.user_criteria.lifetime_net_win.value = parseInt(
				(parseFloat(campaignPayload.user_criteria.lifetime_net_win.value) * getDivisor()).toString()
			);
		}

		if ('balance' in campaignPayload.user_criteria) {
			campaignPayload.user_criteria.balance.value = parseInt(
				(parseFloat(campaignPayload.user_criteria.balance.value) * getDivisor()).toString()
			);
			if ('value_between' in campaignPayload.user_criteria.balance) {
				campaignPayload.user_criteria.balance.value_between = parseInt(
					(parseFloat(campaignPayload.user_criteria.balance.value_between) * getDivisor()).toString()
				);
			}
		}

		if ('average_bet_size' in campaignPayload.user_criteria) {
			campaignPayload.user_criteria.average_bet_size.value = parseInt(
				(parseFloat(campaignPayload.user_criteria.average_bet_size.value) * getDivisor()).toString()
			);
			if ('value_between' in campaignPayload.user_criteria.average_bet_size) {
				campaignPayload.user_criteria.average_bet_size.value_between = parseInt(
					(parseFloat(campaignPayload.user_criteria.average_bet_size.value_between) * getDivisor()).toString()
				);
			}
		}

		if ('greatest_amount_deposited' in campaignPayload.user_criteria) {
			campaignPayload.user_criteria.greatest_amount_deposited.value = parseInt(
				(parseFloat(campaignPayload.user_criteria.greatest_amount_deposited.value) * getDivisor()).toString()
			);
			if ('value_between' in campaignPayload.user_criteria.greatest_amount_deposited) {
				campaignPayload.user_criteria.greatest_amount_deposited.value_between = parseInt(
					(parseFloat(campaignPayload.user_criteria.greatest_amount_deposited.value_between) * getDivisor()).toString()
				);
			}
		}

		if ('remaining_deposit_limit' in campaignPayload.user_criteria) {
			campaignPayload.user_criteria.remaining_deposit_limit.value = parseInt(
				(parseFloat(campaignPayload.user_criteria.remaining_deposit_limit.value) * getDivisor()).toString()
			);
			if ('value_between' in campaignPayload.user_criteria.remaining_deposit_limit) {
				campaignPayload.user_criteria.remaining_deposit_limit.value_between = parseInt(
					(parseFloat(campaignPayload.user_criteria.remaining_deposit_limit.value_between) * getDivisor()).toString()
				);
			}
		}

		if ('net_deposits_7_days' in campaignPayload.user_criteria) {
			campaignPayload.user_criteria.net_deposits_7_days.value = parseInt(
				(parseFloat(campaignPayload.user_criteria.net_deposits_7_days.value) * getDivisor()).toString()
			);
			if ('value_between' in campaignPayload.user_criteria.net_deposits_7_days) {
				campaignPayload.user_criteria.net_deposits_7_days.value_between = parseInt(
					(parseFloat(campaignPayload.user_criteria.net_deposits_7_days.value_between) * getDivisor()).toString()
				);
			}
		}

		if ('net_deposits_14_days' in campaignPayload.user_criteria) {
			campaignPayload.user_criteria.net_deposits_14_days.value = parseInt(
				(parseFloat(campaignPayload.user_criteria.net_deposits_14_days.value) * getDivisor()).toString()
			);
			if ('value_between' in campaignPayload.user_criteria.net_deposits_14_days) {
				campaignPayload.user_criteria.net_deposits_14_days.value_between = parseInt(
					(parseFloat(campaignPayload.user_criteria.net_deposits_14_days.value_between) * getDivisor()).toString()
				);
			}
		}

		if ('net_deposits_30_days' in campaignPayload.user_criteria) {
			campaignPayload.user_criteria.net_deposits_30_days.value = parseInt(
				(parseFloat(campaignPayload.user_criteria.net_deposits_30_days.value) * getDivisor()).toString()
			);
			if ('value_between' in campaignPayload.user_criteria.net_deposits_30_days) {
				campaignPayload.user_criteria.net_deposits_30_days.value_between = parseInt(
					(parseFloat(campaignPayload.user_criteria.net_deposits_30_days.value_between) * getDivisor()).toString()
				);
			}
		}

		if ('net_deposits_90_days' in campaignPayload.user_criteria) {
			campaignPayload.user_criteria.net_deposits_90_days.value = parseInt(
				(parseFloat(campaignPayload.user_criteria.net_deposits_90_days.value) * getDivisor()).toString()
			);
			if ('value_between' in campaignPayload.user_criteria.net_deposits_90_days) {
				campaignPayload.user_criteria.net_deposits_90_days.value_between = parseInt(
					(parseFloat(campaignPayload.user_criteria.net_deposits_90_days.value_between) * getDivisor()).toString()
				);
			}
		}

		if ('net_deposits_lifetime' in campaignPayload.user_criteria) {
			campaignPayload.user_criteria.net_deposits_lifetime.value = parseInt(
				(parseFloat(campaignPayload.user_criteria.net_deposits_lifetime.value) * getDivisor()).toString()
			);
			if ('value_between' in campaignPayload.user_criteria.net_deposits_lifetime) {
				campaignPayload.user_criteria.net_deposits_lifetime.value_between = parseInt(
					(parseFloat(campaignPayload.user_criteria.net_deposits_lifetime.value_between) * getDivisor()).toString()
				);
			}
		}

		if ('total_withdrawals_7_days' in campaignPayload.user_criteria) {
			campaignPayload.user_criteria.total_withdrawals_7_days.value = parseInt(
				(parseFloat(campaignPayload.user_criteria.total_withdrawals_7_days.value) * getDivisor()).toString()
			);
			if ('value_between' in campaignPayload.user_criteria.total_withdrawals_7_days) {
				campaignPayload.user_criteria.total_withdrawals_7_days.value_between = parseInt(
					(parseFloat(campaignPayload.user_criteria.total_withdrawals_7_days.value_between) * getDivisor()).toString()
				);
			}
		}
	}

	return campaignPayload;
}
